/**
 * Tolerating Gateway builds older than the schema this client was written against.
 *
 * Gateway RPC params are closed objects: a property a given build does not know
 * fails the whole call (`invalid sessions.list params: at root: unexpected
 * property 'sortBy'`) rather than being ignored. A third-party client cannot
 * assume which build a user runs, so rather than pinning to some lowest common
 * denominator, calls carry the full modern params and shed whatever a given
 * Gateway rejects — once, then remembered.
 *
 * Only optional params are shed. Every call names the ones that carry meaning
 * (`archived`, `message`, `idempotencyKey`); dropping one of those would turn a
 * rejected call into a silently wrong one, so those rejections surface instead.
 */
import { gatewayCredentialScope } from "@openclaw/gateway-client/browser";

const STORAGE_KEY = "openclaw-messenger:unsupported-params:v1";

/** Bounds the shed-and-retry loop; each pass removes exactly one param. */
const MAX_ATTEMPTS = 8;

type Table = Record<string, string[]>;

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readTable(): Table {
  try {
    const raw = safeStorage()?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Table) : {};
  } catch {
    return {};
  }
}

function writeTable(table: Table): void {
  try {
    safeStorage()?.setItem(STORAGE_KEY, JSON.stringify(table));
  } catch {
    // Storage-blocked contexts re-learn once per session instead.
  }
}

function tableKey(gatewayUrl: string, method: string): string {
  return `${gatewayCredentialScope(gatewayUrl)}|${method}`;
}

export function loadUnsupportedParams(gatewayUrl: string, method: string): string[] {
  const stored = readTable()[tableKey(gatewayUrl, method)];
  return Array.isArray(stored) ? stored.filter((item) => typeof item === "string") : [];
}

function rememberUnsupportedParam(gatewayUrl: string, method: string, param: string): void {
  const table = readTable();
  const key = tableKey(gatewayUrl, method);
  const current = new Set(table[key] ?? []);
  current.add(param);
  table[key] = [...current];
  writeTable(table);
}

export function clearUnsupportedParams(): void {
  try {
    safeStorage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Reads the offending property out of a params-validation rejection.
 *
 * Matched against the method name so an "unexpected property" message about
 * something else entirely cannot make us silently drop a param.
 */
export function readUnexpectedParam(message: unknown, method: string): string | null {
  if (typeof message !== "string") {
    return null;
  }
  if (!message.includes(`invalid ${method} params`) && !message.includes("invalid params")) {
    return null;
  }
  return /unexpected property '([^']+)'/.exec(message)?.[1] ?? null;
}

type Requester = { request<T>(method: string, params?: unknown): Promise<T> };

/**
 * Calls a Gateway method, shedding optional params this Gateway rejects.
 *
 * `protectedParams` are never shed: they either identify the target or carry
 * the intent of the call.
 */
export async function callCompat<T>(
  client: Requester & { url: string },
  method: string,
  params: Record<string, unknown>,
  protectedParams: readonly string[] = [],
): Promise<T> {
  const dropped = new Set(loadUnsupportedParams(client.url, method));
  const protectedSet = new Set(protectedParams);

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const payload: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
      if (!dropped.has(key)) {
        payload[key] = value;
      }
    }

    try {
      return await client.request<T>(method, payload);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const offending = readUnexpectedParam(message, method);
      if (
        !offending ||
        protectedSet.has(offending) ||
        dropped.has(offending) ||
        !(offending in payload)
      ) {
        throw error;
      }
      dropped.add(offending);
      rememberUnsupportedParam(client.url, method, offending);
    }
  }

  // Every pass removed a param and the call still failed for another reason.
  return await client.request<T>(method, params);
}
